import _extends from "@babel/runtime/helpers/extends";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { string, number, shape, bool } from 'prop-types';
import { Link } from 'react-router-dom';
import { UNCONSTRAINED_SIZE_KEY } from '@magento/peregrine/lib/talons/Image/useImage';
import { useGalleryItem } from '@magento/peregrine/lib/talons/Gallery/useGalleryItem';
import resourceUrl from '@magento/peregrine/lib/util/makeUrl';
import { useWindowSize } from '@magento/peregrine/lib/hooks/useWindowSize';
import { useUserContext } from '@magento/peregrine/lib/context/user/';
import Price from '@magento/venia-ui/lib/components/Price';
import { Image, Button } from '..';
import GalleryItemShimmer from '@magento/venia-ui/lib/components/Gallery/item.shimmer';
import classes from './productItem.module.css';
import WishlistGalleryButton from 'src/overrides/venia-ui/components/Wishlist/AddToListPdpButton';
import SignInModal from '../Favorites/SignInModal';
import DownloadImage from '../../assets/download-btn-plp.svg';
import DownloadImageFull from '../../assets/download-full-btn-plp.svg';
import { useMutation } from '@apollo/client';
import { ADD_ITEM_TO_DOWNLOAD, REMOVE_ITEM_FROM_DOWNLOAD } from '../../graphql/downloadImages/actions.gql';
import LoadingIndicator from '@magento/venia-ui/lib/components/LoadingIndicator';
import { FormattedMessage } from 'react-intl';
import { useGalleryItemCollection } from './useGalleryItemCollection';
import useToast from '../../hooks/useToast';
import { useAppContext } from '@magento/peregrine/lib/context/app';
import MiddleDialog from '../Favorites/MiddleDialog'; // The placeholder image is 4:5, so we should make sure to size our product
// images appropriately.

var IMAGE_WIDTH = 300;
var IMAGE_HEIGHT = 375; // Gallery switches from two columns to three at 640px.

var IMAGE_WIDTHS = new Map().set(640, IMAGE_WIDTH).set(UNCONSTRAINED_SIZE_KEY, 840);

var checkIfValidValue = function checkIfValidValue(value) {
  return value && value !== 'NaN';
};

export var getDimensions = function getDimensions(item) {
  return "".concat(checkIfValidValue(item === null || item === void 0 ? void 0 : item.hf_width) ? "".concat(item === null || item === void 0 ? void 0 : item.hf_width, "\"").concat(checkIfValidValue(item.hf_height) || checkIfValidValue(item.hf_depth_length) ? ' x ' : '') : '').concat(checkIfValidValue(item.hf_height) ? "".concat(item === null || item === void 0 ? void 0 : item.hf_height, "\"").concat(checkIfValidValue(item.hf_depth_length) ? ' x ' : '') : '').concat(checkIfValidValue(item.hf_depth_length) ? "".concat(item === null || item === void 0 ? void 0 : item.hf_depth_length, "\"") : '');
};

var ProductItem = function ProductItem(_ref) {
  var _item$sku, _priceRange$price_ran, _priceRange$price_ran2, _priceRange$price_ran3, _priceRange$price_ran4, _priceRange$price_ran5, _priceRange$price_ran6, _priceRange$price_ran7, _priceRange$price_ran8, _priceRange$price_ran9, _priceRange$price_ran10, _priceRange$price_ran11, _priceRange$price_ran12, _priceRange$price_ran13, _priceRange$price_ran14, _priceRange$price_ran15, _priceRange$price_ran16, _priceRange$price_ran17, _priceRange$price_ran18, _priceRange$price_ran19, _priceRange$price_ran20, _priceRange$price_ran21, _priceRange$price_ran22, _priceRange$price_ran23, _priceRange$price_ran24;

  var _ref$isCollectionBtnA = _ref.isCollectionBtnAllowed,
      isCollectionBtnAllowed = _ref$isCollectionBtnA === void 0 ? false : _ref$isCollectionBtnA,
      _ref$toggleCollection = _ref.toggleCollection,
      toggleCollection = _ref$toggleCollection === void 0 ? function () {} : _ref$toggleCollection,
      storeConfig = _ref.storeConfig,
      collectionVisible = _ref.collectionVisible,
      item = _ref.item,
      priceRange = _ref.priceRange,
      downloadImages = _ref.downloadImages,
      isDownload = _ref.isDownload,
      isFiltersHidden = _ref.isFiltersHidden,
      refetch = _ref.refetch,
      _ref$handleLinkClick = _ref.handleLinkClick,
      handleLinkClick = _ref$handleLinkClick === void 0 ? function () {} : _ref$handleLinkClick,
      index = _ref.index,
      imageItem = _ref.imageItem,
      productMarkerId = _ref.productMarkerId,
      persistScrollPosition = _ref.persistScrollPosition,
      handleProductScroll = _ref.handleProductScroll,
      isProductScrolled = _ref.isProductScrolled,
      isInFavorites = _ref.isInFavorites,
      refetchFavorites = _ref.refetchFavorites;

  var _useToast = useToast(),
      addMessage = _useToast.addMessage;

  var productItemRef = useRef();

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      modalData = _useState2[0],
      setModalData = _useState2[1];

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      isWishlistDialogOpen = _useState4[0],
      setIsWishlistDialogOpen = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isHoveredItem = _useState6[0],
      setIsHoveredItem = _useState6[1];

  var _useWindowSize = useWindowSize(),
      innerWidth = _useWindowSize.innerWidth;

  var isSmallDevice = innerWidth < 980;

  var _useUserContext = useUserContext(),
      _useUserContext2 = _slicedToArray(_useUserContext, 1),
      isUserSignedIn = _useUserContext2[0].isSignedIn;

  var _useAppContext = useAppContext(),
      _useAppContext2 = _slicedToArray(_useAppContext, 2),
      closeDrawer = _useAppContext2[1].closeDrawer;

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      loading = _useState8[0],
      setLoading = _useState8[1];

  var _useState9 = useState({
    remove: true,
    errorRemoving: false,
    successAdding: false,
    errorAdding: false
  }),
      _useState10 = _slicedToArray(_useState9, 2),
      downloadStatus = _useState10[0],
      setDownloadStatus = _useState10[1];

  var _useGalleryItemCollec = useGalleryItemCollection(),
      handleSubmit = _useGalleryItemCollec.handleSubmit,
      crosssell_products = _useGalleryItemCollec.collectionData,
      crosssell_products_loading = _useGalleryItemCollec.loading;

  useEffect(function () {
    toggleCollection(crosssell_products, index);
  }, [crosssell_products]);

  var _useState11 = useState(null),
      _useState12 = _slicedToArray(_useState11, 2),
      activeItem = _useState12[0],
      setActiveItem = _useState12[1];

  var _useMutation = useMutation(ADD_ITEM_TO_DOWNLOAD),
      _useMutation2 = _slicedToArray(_useMutation, 1),
      addItemToDownloadMethod = _useMutation2[0];

  var _useMutation3 = useMutation(REMOVE_ITEM_FROM_DOWNLOAD),
      _useMutation4 = _slicedToArray(_useMutation3, 1),
      removeItemFromDownloadMethod = _useMutation4[0];

  var _useGalleryItem = useGalleryItem({
    storeConfig: storeConfig,
    item: item
  }),
      wishlistButtonProps = _useGalleryItem.wishlistButtonProps;

  var isOutletProduct = item === null || item === void 0 ? void 0 : (_item$sku = item.sku) === null || _item$sku === void 0 ? void 0 : _item$sku.startsWith('OUT-');
  useEffect(function () {
    if (productMarkerId == null || productMarkerId === undefined) return;else if (productMarkerId === item.id && !isProductScrolled) {
      productItemRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'center'
      });
      handleProductScroll === null || handleProductScroll === void 0 ? void 0 : handleProductScroll();
    } else return;
  }, [productMarkerId]);
  useEffect(function () {
    var clearDownloadStatus = setTimeout(function () {
      return setDownloadStatus({
        remove: true,
        errorRemoving: false,
        successAdding: false,
        errorAdding: false
      });
    }, 3000);
    return function () {
      return clearTimeout(clearDownloadStatus);
    };
  }, []);

  var addItemToDownload = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(sku) {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              setLoading(true);
              _context.next = 3;
              return addItemToDownloadMethod({
                variables: {
                  input: {
                    product_sku: sku
                  }
                }
              });

            case 3:
              try {
                setDownloadStatus({
                  remove: false,
                  errorRemoving: false,
                  successAdding: true,
                  errorAdding: false
                });
                addMessage('success', 'Successfully added to downloads');
                refetch();
                setLoading(false);
              } catch (error) {
                setLoading(false);
                setDownloadStatus({
                  remove: false,
                  errorRemoving: false,
                  successAdding: false,
                  errorAdding: true
                });
              }

            case 4:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function addItemToDownload(_x) {
      return _ref2.apply(this, arguments);
    };
  }();

  var removeItemFromDownload = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(sku) {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              setLoading(true);
              _context2.next = 3;
              return removeItemFromDownloadMethod({
                variables: {
                  input: {
                    product_sku: sku
                  }
                }
              });

            case 3:
              try {
                refetch();
                setLoading(false);
                setDownloadStatus({
                  remove: false,
                  errorRemoving: false,
                  successAdding: false,
                  errorAdding: false
                });
                addMessage('success', 'Successfully removed from downloads');
              } catch (error) {
                setLoading(false);
                setDownloadStatus({
                  remove: false,
                  errorRemoving: true,
                  successAdding: false,
                  errorAdding: false
                });
              }

            case 4:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));

    return function removeItemFromDownload(_x2) {
      return _ref3.apply(this, arguments);
    };
  }();

  var isInDownload = useMemo(function () {
    return downloadImages === null || downloadImages === void 0 ? void 0 : downloadImages.find(function (data) {
      var _data$product;

      return (data === null || data === void 0 ? void 0 : (_data$product = data.product) === null || _data$product === void 0 ? void 0 : _data$product.sku) === (item === null || item === void 0 ? void 0 : item.sku);
    });
  }, [downloadImages]);

  if (!item) {
    return React.createElement(GalleryItemShimmer, {
      classes: classes
    });
  }

  var productUrlSuffix = storeConfig && storeConfig.product_url_suffix;
  var closeModal = useCallback(function () {
    return setModalData(null);
  });
  var isSignInModalOpen = modalData === 'SIGN_IN';
  var isMiddleDialogOpen = modalData === 'MIDDLE_DIALOG';
  var wishlistButton = wishlistButtonProps ? React.createElement(WishlistGalleryButton, _extends({}, wishlistButtonProps, {
    product: item,
    setModalData: setModalData,
    isWishlistDialogOpen: isWishlistDialogOpen,
    setIsWishlistDialogOpen: setIsWishlistDialogOpen,
    isSmallIcon: true,
    activeItem: activeItem,
    setActiveItem: setActiveItem,
    isInFavorites: isInFavorites,
    refetchFavorites: refetchFavorites,
    isPlp: true
  })) : null; // btn is always shown when id of product item is equal with collectio id

  var isCollectiondSliderVisible = collectionVisible;
  var buttonText = crosssell_products_loading ? 'LOADING COLLECTION' : isCollectiondSliderVisible ? ' HIDE COLLECTION' : 'SHOW COLLECTION';
  var isCollectionBtnVisible = isSmallDevice ? true : false;

  if (isCollectiondSliderVisible) {
    isCollectionBtnVisible = true;
  } else if (isHoveredItem) {
    isCollectionBtnVisible = true;
  }

  var showCollectionButton = useMemo(function () {
    var _collectionItems, _collectionItems2;

    var collectionItems = item === null || item === void 0 ? void 0 : item.crosssell_products;
    collectionItems = (_collectionItems = collectionItems) === null || _collectionItems === void 0 ? void 0 : _collectionItems.filter(function (item) {
      var _item$sku2;

      return !(item !== null && item !== void 0 && (_item$sku2 = item.sku) !== null && _item$sku2 !== void 0 && _item$sku2.startsWith('OUT-'));
    });
    return !isOutletProduct && isCollectionBtnVisible && ((_collectionItems2 = collectionItems) === null || _collectionItems2 === void 0 ? void 0 : _collectionItems2.length) > 0;
  }); // TODO handle latter is downloaded or not

  var downloadBtnClasses = isDownload ? classes.downloadedBtn : classes.downloadBtn;
  var name = item.name,
      price_range = item.price_range,
      small_image = item.small_image,
      url_key = item.url_key,
      sku = item.sku,
      url = item.url; // const { url: smallImageURL } = small_image;

  var smallImageURL = small_image === null || small_image === void 0 ? void 0 : small_image.url; // ?? small_image; // UNCOMMENT FOR CF
  // const smallImageURL = imageItem?.image?.url ?? small_image?.url; // UNCOMMENT FOR CF

  var urlPath = url ? url : url_key; // const productLink = resourceUrl(`/${url_key}${productUrlSuffix || ''}`);

  var productLink = resourceUrl("/".concat(urlPath).concat(productUrlSuffix || ''));
  var showDiscountedPrice = isOutletProduct && parseFloat(priceRange === null || priceRange === void 0 ? void 0 : (_priceRange$price_ran = priceRange.price_range) === null || _priceRange$price_ran === void 0 ? void 0 : (_priceRange$price_ran2 = _priceRange$price_ran.product_min_price) === null || _priceRange$price_ran2 === void 0 ? void 0 : (_priceRange$price_ran3 = _priceRange$price_ran2.regular_price) === null || _priceRange$price_ran3 === void 0 ? void 0 : _priceRange$price_ran3.value) > parseFloat(priceRange === null || priceRange === void 0 ? void 0 : (_priceRange$price_ran4 = priceRange.price_range) === null || _priceRange$price_ran4 === void 0 ? void 0 : (_priceRange$price_ran5 = _priceRange$price_ran4.product_min_price) === null || _priceRange$price_ran5 === void 0 ? void 0 : (_priceRange$price_ran6 = _priceRange$price_ran5.final_price) === null || _priceRange$price_ran6 === void 0 ? void 0 : _priceRange$price_ran6.value);
  var showFrom = isOutletProduct && parseFloat(priceRange === null || priceRange === void 0 ? void 0 : (_priceRange$price_ran7 = priceRange.price_range) === null || _priceRange$price_ran7 === void 0 ? void 0 : (_priceRange$price_ran8 = _priceRange$price_ran7.product_min_price) === null || _priceRange$price_ran8 === void 0 ? void 0 : (_priceRange$price_ran9 = _priceRange$price_ran8.final_price) === null || _priceRange$price_ran9 === void 0 ? void 0 : _priceRange$price_ran9.value) < parseFloat(priceRange === null || priceRange === void 0 ? void 0 : (_priceRange$price_ran10 = priceRange.price_range) === null || _priceRange$price_ran10 === void 0 ? void 0 : (_priceRange$price_ran11 = _priceRange$price_ran10.product_max_price) === null || _priceRange$price_ran11 === void 0 ? void 0 : (_priceRange$price_ran12 = _priceRange$price_ran11.final_price) === null || _priceRange$price_ran12 === void 0 ? void 0 : _priceRange$price_ran12.value);
  var itemDimensions = getDimensions(item);
  var OutletComponent = isOutletProduct ? React.createElement("div", {
    className: classes.outletMark
  }, "OUTLET") : React.createElement(React.Fragment, null);
  return React.createElement(React.Fragment, null, React.createElement("div", {
    className: isCollectiondSliderVisible ? classes.root_active : classes.root,
    "aria-live": "polite",
    "aria-busy": "false",
    onMouseEnter: function onMouseEnter() {
      return setIsHoveredItem(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setIsHoveredItem(false);
    },
    ref: productItemRef
  }, React.createElement("div", {
    className: classes.content
  }, OutletComponent, isUserSignedIn ? React.createElement("div", {
    className: loading ? classes.center : downloadBtnClasses
  }, loading ? React.createElement(LoadingIndicator, null, React.createElement(FormattedMessage, {
    id: 'addingToDownloads',
    defaultMessage: 'Loading...'
  })) : React.createElement(React.Fragment, null, React.createElement("div", {
    className: classes.mainDownloadImgWrapper
  }, !isInDownload ? React.createElement("div", {
    className: isFiltersHidden ? "".concat(classes.tooltipWrapper, " ").concat(classes.tooltipPosition) : classes.tooltipWrapper
  }, "Download product image") : React.createElement(React.Fragment, null, downloadStatus.remove && React.createElement("div", {
    className: isFiltersHidden ? "".concat(classes.tooltipWrapperCompleted, " ").concat(classes.tooltipPosition) : classes.tooltipWrapperCompleted
  }, "Remove from downloads"), downloadStatus.errorRemoving && React.createElement("div", {
    className: isFiltersHidden ? "".concat(classes.tooltipWrapperErrorRemoving, " ").concat(classes.tooltipPosition) : classes.tooltipWrapperErrorRemoving
  }, "Error removing image from download"), downloadStatus.successAdding && React.createElement("div", {
    className: isFiltersHidden ? "".concat(classes.tooltipWrapperSuccess, " ").concat(classes.tooltipPosition) : classes.tooltipWrapperSuccess
  }, "Successfully added to downloads"), downloadStatus.errorAdding && React.createElement("div", {
    className: isFiltersHidden ? "".concat(classes.tooltipWrapperErrorAdding, " ").concat(classes.tooltipPosition) : classes.tooltipWrapperErrorAdding
  }, "Error adding image for download")), React.createElement("div", {
    onClick: function onClick() {
      return isInDownload ? removeItemFromDownload(item === null || item === void 0 ? void 0 : item.sku) : addItemToDownload(item === null || item === void 0 ? void 0 : item.sku);
    }
  }, React.createElement("img", {
    src: !isInDownload ? DownloadImage : DownloadImageFull,
    alt: "download-image",
    className: classes.downloadImgProduct
  }))))) : null, isOutletProduct ? null : React.createElement("div", {
    className: classes.addFavouriteBtn
  }, wishlistButton, " "), React.createElement(Link, {
    to: productLink,
    onClick: function onClick() {
      handleLinkClick();
      persistScrollPosition === null || persistScrollPosition === void 0 ? void 0 : persistScrollPosition(item.id);
    },
    className: classes.images,
    "aria-label": "Go to product ".concat(name)
  }, React.createElement(Image, {
    alt: name,
    classes: {
      image: classes.image,
      loaded: classes.imageLoaded,
      notLoaded: classes.imageNotLoaded,
      container: classes.imageContainer
    },
    resource: smallImageURL,
    height: IMAGE_HEIGHT,
    widths: IMAGE_WIDTHS
  })), React.createElement("div", {
    className: "pl-1.5 pr-3 pb-3 pt-3 md_p-5 flex-1 flex flex-col justify-start"
  }, React.createElement("div", {
    className: "mt-1"
  }, React.createElement(Link, {
    to: productLink,
    onClick: function onClick() {
      handleLinkClick();
      persistScrollPosition === null || persistScrollPosition === void 0 ? void 0 : persistScrollPosition(item.id);
    },
    className: classes.name,
    "aria-label": "Go to product ".concat(name),
    title: name
  }, React.createElement("span", null, name))), React.createElement("div", null, React.createElement("div", {
    className: classes.skuContent
  }, React.createElement("p", {
    className: "leading-2"
  }, "Base Item ", sku), React.createElement("p", {
    className: "leading-2"
  }, itemDimensions.replace(/ x $/, '').replace('null" x', '').replace('x null"', ''))), React.createElement("div", {
    className: classes.price
  }, React.createElement("div", null, !!priceRange && React.createElement(React.Fragment, null, showFrom && React.createElement("span", {
    className: "pr-2"
  }, "From"), showDiscountedPrice && React.createElement(Price, {
    classes: {
      root: 'text-base leading-2 md_leading-171 font-normal text-black line-through inline mr-2'
    },
    value: parseFloat(priceRange === null || priceRange === void 0 ? void 0 : (_priceRange$price_ran13 = priceRange.price_range) === null || _priceRange$price_ran13 === void 0 ? void 0 : (_priceRange$price_ran14 = _priceRange$price_ran13.product_min_price) === null || _priceRange$price_ran14 === void 0 ? void 0 : (_priceRange$price_ran15 = _priceRange$price_ran14.regular_price) === null || _priceRange$price_ran15 === void 0 ? void 0 : _priceRange$price_ran15.value),
    currencyCode: priceRange === null || priceRange === void 0 ? void 0 : (_priceRange$price_ran16 = priceRange.price_range) === null || _priceRange$price_ran16 === void 0 ? void 0 : (_priceRange$price_ran17 = _priceRange$price_ran16.product_min_price) === null || _priceRange$price_ran17 === void 0 ? void 0 : (_priceRange$price_ran18 = _priceRange$price_ran17.regular_price) === null || _priceRange$price_ran18 === void 0 ? void 0 : _priceRange$price_ran18.currency
  }), React.createElement(Price, {
    classes: {
      root: 'text-base leading-2 md_leading-171 font-normal' + (showDiscountedPrice ? ' block text-red sm_inline' : '')
    },
    value: parseFloat(priceRange === null || priceRange === void 0 ? void 0 : (_priceRange$price_ran19 = priceRange.price_range) === null || _priceRange$price_ran19 === void 0 ? void 0 : (_priceRange$price_ran20 = _priceRange$price_ran19.product_min_price) === null || _priceRange$price_ran20 === void 0 ? void 0 : (_priceRange$price_ran21 = _priceRange$price_ran20.final_price) === null || _priceRange$price_ran21 === void 0 ? void 0 : _priceRange$price_ran21.value),
    currencyCode: priceRange === null || priceRange === void 0 ? void 0 : (_priceRange$price_ran22 = priceRange.price_range) === null || _priceRange$price_ran22 === void 0 ? void 0 : (_priceRange$price_ran23 = _priceRange$price_ran22.product_min_price) === null || _priceRange$price_ran23 === void 0 ? void 0 : (_priceRange$price_ran24 = _priceRange$price_ran23.final_price) === null || _priceRange$price_ran24 === void 0 ? void 0 : _priceRange$price_ran24.currency
  })), !priceRange && React.createElement(Price, {
    classes: {
      root: 'text-base leading-2 md_leading-171 font-normal' + (showDiscountedPrice ? ' text-red inline' : '')
    },
    value: parseFloat(price_range.maximum_price.regular_price.value),
    currencyCode: price_range.maximum_price.regular_price.currency
  }), React.createElement("div", {
    className: classes.flagWrapper
  }, (item === null || item === void 0 ? void 0 : item.hf_new_designs) === 'Yes' && React.createElement("span", {
    className: classes.flag
  }, "New"), (item === null || item === void 0 ? void 0 : item.hf_designer_preference) === 'Yes' && React.createElement("span", {
    className: classes.flag
  }, "Designer's Choice"), (item === null || item === void 0 ? void 0 : item.hf_best_seller) === 'Yes' && React.createElement("span", {
    className: classes.flag
  }, "Best Seller"))))))), isCollectionBtnAllowed && React.createElement("div", {
    className: classes.actionsContainer
  }, showCollectionButton && React.createElement(Button, {
    priority: "low",
    fullWidth: true,
    size: "small",
    classes: {
      root: classes.btnRoot,
      content: classes.contentBtn
    },
    onClick: function onClick() {
      return collectionVisible ? toggleCollection(item, index) : handleSubmit(item === null || item === void 0 ? void 0 : item.sku);
    },
    "aria-label": buttonText
  }, buttonText))), activeItem && React.createElement(SignInModal, {
    isOpen: isSignInModalOpen,
    item: (activeItem === null || activeItem === void 0 ? void 0 : activeItem.sku) === (item === null || item === void 0 ? void 0 : item.sku) && item,
    closeModal: closeModal,
    itemOption: wishlistButtonProps === null || wishlistButtonProps === void 0 ? void 0 : wishlistButtonProps.item
  }), isMiddleDialogOpen && React.createElement(MiddleDialog, {
    setIsWishlistDialogOpen: setIsWishlistDialogOpen,
    autoClose: closeModal
  }));
};

ProductItem.propTypes = {
  classes: shape({
    image: string,
    imageLoaded: string,
    imageNotLoaded: string,
    imageContainer: string,
    images: string,
    name: string,
    price: string,
    root: string,
    content: string,
    actionsContainer: string,
    skuContent: string
  }),
  item: shape({
    uid: string,
    name: string.isRequired,
    small_image: shape({
      url: string.isRequired
    }),
    stock_status: string.isRequired,
    // type_id: string.isRequired,
    url_key: string.isRequired,
    url_suffix: string,
    sku: string.isRequired,
    price_range: shape({
      maximum_price: shape({
        regular_price: shape({
          value: number.isRequired,
          currency: string.isRequired
        }).isRequired
      }).isRequired
    }).isRequired
  }),
  storeConfig: shape({
    magento_wishlist_general_is_enabled: string.isRequired,
    product_url_suffix: string
  }),
  isCollectionBtnAllowed: bool // describes is show collection button allowed

};
export default ProductItem;